import { Badge, Radio } from "antd";
import { defineMessages, useIntl } from "react-intl";

import { IChoice } from "@/modules/assessments/types/test.interface";

import { TestElementMode } from "@/shared/components/candidate/scenes/TestElement/TestElement";
import { getScaleRepresentation, getUniqueKeysValues } from "@/shared/components/testManagement/TestElementInfoBlock";

import { findKeys } from "@/ui-components/testElementComponents/StatementsRatingGroup/StatementsRatingGroup";
import { getScaleBadgeColor } from "@/ui-components/testElementComponents/TrueFalseRadioGroup/TrueFalseRadioGroup";

import "./LikelyTrueFalseRadioGroupe.css";

interface ILikelyTrueFalseRadioGroup {
  id: number;
  mode?: TestElementMode;
  currentAnswer: string | boolean | undefined;
  choices: IChoice[];
  onRadioChange: (e: any) => void;
  disabled?: boolean;
}

enum LikelyTrueFalseChoices {
  C1 = "T",
  C2 = "LT",
  C3 = "LI",
  C4 = "LF",
  C5 = "F",
}

// Localized display labels for the scale. The enum values above stay as the submitted answer values;
// only the visible label is localized. Abbreviations: T = true, LT = likely true, LI = lack of
// information, LF = likely false, F = false.
const choiceLabels = defineMessages({
  c1: { id: "candidate.answer.likely.true", defaultMessage: "T" },
  c2: { id: "candidate.answer.likely.likelyTrue", defaultMessage: "LT" },
  c3: { id: "candidate.answer.likely.lackInfo", defaultMessage: "LI" },
  c4: { id: "candidate.answer.likely.likelyFalse", defaultMessage: "LF" },
  c5: { id: "candidate.answer.likely.false", defaultMessage: "F" },
});

export function LikelyTrueFalseRadioGroup({
  id,
  mode = TestElementMode.Candidate,
  currentAnswer,
  choices,
  onRadioChange,
  disabled = false,
}: ILikelyTrueFalseRadioGroup) {
  const intl = useIntl();
  const isAdminView = mode === TestElementMode.Admin;
  const showBadge = isAdminView;

  return (
    <div className="testElementContainer" style={{ width: showBadge ? "60%" : "100%" }}>
      {showBadge && (
        <div className="badges">
          {choices.map(({ key, scales }, index) => {
            const scaleRepresentation =
              scales.length === 0 ? "" : getScaleRepresentation(getUniqueKeysValues([scales]));
            const badgeColor = getScaleBadgeColor(scaleRepresentation);
            return (
              <Badge key={key} count={scaleRepresentation || 0} offset={[20, -10]} color={badgeColor}>
                <p style={{ width: index === 0 || 4 ? 30 : index === 1 || 3 ? 24 : 18 }}></p>
              </Badge>
            );
          })}
        </div>
      )}

      <Radio.Group
        id={String(id)}
        name={String(id)}
        className="radioGroup"
        style={{ marginTop: showBadge ? 0 : "2em" }}
        onChange={onRadioChange}
        value={String(currentAnswer)}
        disabled={disabled}
      >
        <Radio.Button
          id={findKeys(choices, LikelyTrueFalseChoices.C1)}
          value={LikelyTrueFalseChoices.C1}
          className="large"
        />
        <Radio.Button
          id={findKeys(choices, LikelyTrueFalseChoices.C2)}
          value={LikelyTrueFalseChoices.C2}
          className="medium"
        />
        <Radio.Button
          id={findKeys(choices, LikelyTrueFalseChoices.C3)}
          value={LikelyTrueFalseChoices.C3}
          className="small"
        />
        <Radio.Button
          id={findKeys(choices, LikelyTrueFalseChoices.C4)}
          value={LikelyTrueFalseChoices.C4}
          className="medium"
        />

        <Radio.Button
          id={findKeys(choices, LikelyTrueFalseChoices.C5)}
          value={LikelyTrueFalseChoices.C5}
          className="large"
        />
      </Radio.Group>
      <div className="radioGroup textGroup" style={{ marginTop: "25px" }}>
        <p className="largeParagraph">
          {isAdminView ? LikelyTrueFalseChoices.C1 : intl.formatMessage(choiceLabels.c1)}
        </p>
        <p className="mediumParagraph">
          {isAdminView ? LikelyTrueFalseChoices.C2 : intl.formatMessage(choiceLabels.c2)}
        </p>
        <p className="smallParagraph">
          {isAdminView ? LikelyTrueFalseChoices.C3 : intl.formatMessage(choiceLabels.c3)}
        </p>
        <p className="mediumParagraph">
          {isAdminView ? LikelyTrueFalseChoices.C4 : intl.formatMessage(choiceLabels.c4)}
        </p>
        <p className="largeParagraph">
          {isAdminView ? LikelyTrueFalseChoices.C5 : intl.formatMessage(choiceLabels.c5)}
        </p>
      </div>
    </div>
  );
}
